import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Target, Lightbulb, TrendingUp, CheckCircle2, Users, Eye, ShoppingCart, BarChart3, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
};

const staggerContainer = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true, margin: "-100px" },
  transition: { staggerChildren: 0.1 }
};

const staggerItem = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

// Case studies navigation order
const caseStudyOrder = ['findyourfit', 'goat', 'upre', 'ouzo', 'house-of-resham', 'bagheera', 'garden-leaf', 'golden-locks', 'focus-apparels'];

const caseStudiesData = {
  'upre': {
    title: 'UPRÉ BY 1559 AD',
    tagline: 'Premium Rooftop Dining',
    industry: 'HOSPITALITY | PREMIUM DINING',
    heroImage: '/upre-lakeside.jpg',
    galleryImages: ['/upre-lakeside.jpg'],
    description: 'Upré by 1559 AD is a renowned rooftop dining destination in Udaipur, located on the terrace of the iconic Lake Pichola Hotel. Overlooking Lake Pichola and the historic skyline of the city including the City Palace and surrounding ghats, the restaurant offers one of the most distinctive dining settings in the region.',
    objective: 'Strengthen the restaurant\'s digital identity and position it as a premier lakeside dining experience within Udaipur\'s vibrant hospitality landscape.',
    approach: [
      'Capturing the unique atmosphere that defines Upré through visual storytelling',
      'Highlighting the restaurant\'s panoramic lake views and open-air terrace dining',
      'Showcasing the elegant evening ambience that attracts both travellers and local patrons',
      'Emphasizing experiential storytelling around the destination aspect',
      'Creating content featuring cuisine, cocktails and sunset dining experience'
    ],
    outcome: 'The resulting communication strengthened Upré\'s digital presence while reinforcing its identity as one of Udaipur\'s most memorable rooftop dining destinations.',
    services: ['Social Media Management', 'Content Creation', 'Visual Storytelling', 'Brand Positioning'],
    results: [
      { label: 'SOCIAL GROWTH', value: '180%', icon: Users },
      { label: 'ENGAGEMENT RATE', value: '14%', icon: TrendingUp },
      { label: 'BRAND REACH', value: '600K+', icon: Eye },
      { label: 'DIRECT INQUIRIES', value: '+95%', icon: BarChart3 }
    ],
    testimonial: 'Zenith understood the essence of our rooftop experience and translated it beautifully into digital storytelling that resonates with our discerning clientele.'
  },
  'ouzo': {
    title: 'OUZO BAR & KITCHEN',
    tagline: 'Contemporary Nightlife Destination',
    industry: 'HOSPITALITY | BAR & RESTAURANT',
    heroImage: '/ouzo-chef-1.jpg',
    galleryImages: ['/ouzo-chef-1.jpg', '/ouzo-chef-2.jpg', '/ouzo-food.jpg'],
    description: 'Ouzo Bar & Kitchen is a contemporary dining and nightlife destination designed to bring together vibrant ambience, curated cuisine and an energetic social atmosphere.',
    objective: 'Establish a distinctive digital identity for the brand and position it as a dynamic social destination within the city\'s evolving hospitality landscape.',
    approach: [
      'Translating the restaurant\'s energetic character into compelling visual communication',
      'Content strategy highlighting the bar experience, signature dishes, and cocktails',
      'Showcasing the lively ambience that defines the venue',
      'Structured digital storytelling emphasizing experience-driven dining',
      'Making the space appealing to a younger, socially active audience'
    ],
    outcome: 'The resulting communication strengthened Ouzo Bar & Kitchen\'s digital presence and reinforced its positioning as a vibrant destination.',
    services: ['Social Media Management', 'Content Creation', 'Campaign Design', 'Digital Storytelling'],
    results: [
      { label: 'SOCIAL FOLLOWING', value: '220%', icon: Users },
      { label: 'ENGAGEMENT', value: '16%', icon: TrendingUp },
      { label: 'BRAND REACH', value: '450K+', icon: Eye },
      { label: 'FOOTFALL INCREASE', value: '+85%', icon: BarChart3 }
    ],
    testimonial: 'Zenith captured our vibrant energy perfectly and helped us connect with the young, social crowd we wanted to attract.'
  },
  'findyourfit': {
    title: 'FINDYOURFIT',
    tagline: 'Fitness & Lifestyle Platform',
    industry: 'FITNESS & LIFESTYLE PLATFORM',
    heroImage: '/findyourfit-malaika.webp',
    galleryImages: ['/findyourfit-malaika.webp', '/findyourfit-yoga.webp', '/find-your-fit-event.jpg'],
    description: 'FindYourFit is a fitness-focused platform designed to connect individuals with the right training programs, coaches and wellness solutions suited to their goals and lifestyle.',
    objective: 'Drive targeted user acquisition and increase platform visibility through structured digital advertising campaigns.',
    approach: [
      'Focused on performance-driven marketing through carefully planned Meta advertising campaigns',
      'Audience segmentation and creative testing to reach fitness-interested users',
      'Campaign optimisation to target users actively interested in fitness',
      'Campaign creatives designed to communicate clarity of offering',
      'Encouraging immediate engagement from potential users'
    ],
    outcome: 'Through continuous monitoring and optimisation, the campaigns helped improve lead generation and strengthen FindYourFit\'s ability to reach a relevant fitness-focused audience.',
    services: ['Performance Marketing', 'Meta Ads', 'Audience Segmentation', 'Creative Testing', 'Campaign Optimisation'],
    results: [
      { label: 'COMMUNITY MEMBERS', value: '25K+', icon: Users },
      { label: 'EVENT ATTENDANCE', value: '8K+', icon: Calendar },
      { label: 'ENGAGEMENT', value: '22%', icon: TrendingUp },
      { label: 'BRAND REACH', value: '1M+', icon: Eye }
    ],
    testimonial: 'Zenith approaches growth with structured experimentation and disciplined optimisation. Their understanding of audience segmentation has consistently improved efficiency metrics for us. - Arushi Lohani'
  },
  'goat': {
    title: 'GOAT',
    tagline: 'Elevated Dining & Cocktails',
    industry: 'HOSPITALITY | RESTAURANT & NIGHTLIFE',
    heroImage: '/goat-new.jpg',
    galleryImages: ['/goat-new.jpg', '/goat-bhaichung.jpg', '/goat-collage.jpg'],
    description: 'GOAT is a contemporary dining and nightlife destination located in the heart of Siliguri. Conceived as an elevated social space, the brand blends modern interiors, vibrant music culture and curated cuisine.',
    objective: 'Establish a distinctive digital presence for the brand and position it as one of the city\'s most recognizable lifestyle destinations.',
    approach: [
      'Translating the restaurant\'s energetic atmosphere into compelling visual communication',
      'Content designed to highlight venue ambience, signature dishes, and cocktail culture',
      'Showcasing nightlife experiences through dynamic visuals',
      'Carefully curated social media storytelling',
      'Creative campaign design resonating with young, experience-driven audience'
    ],
    outcome: 'The communication helped strengthen GOAT\'s digital identity, enhancing its visibility as a premium dining and social destination in Siliguri.',
    services: ['Social Media Management', 'Content Creation', 'Campaign Design', 'Brand Positioning', 'Digital Marketing'],
    results: [
      { label: 'SOCIAL GROWTH', value: '250%', icon: Users },
      { label: 'ENGAGEMENT', value: '12.5%', icon: TrendingUp },
      { label: 'BRAND AWARENESS', value: '+180%', icon: Eye },
      { label: 'REACH', value: '500K+', icon: BarChart3 }
    ],
    testimonial: 'GOAT has become one of Siliguri\'s most recognized lifestyle destinations, with a digital presence that perfectly captures its vibrant atmosphere.'
  },
  'house-of-resham': {
    title: 'HOUSE OF RESHAM',
    tagline: 'Pret. Diffusion. Couture.',
    industry: 'FASHION & LIFESTYLE',
    heroImage: '/house-of-resham-new.jpg',
    description: 'House of Resham is a contemporary fashion label specialising in premium ethnic wear for women and children. The brand blends traditional craftsmanship with modern silhouettes.',
    objective: 'Strengthen the brand\'s digital identity and present its collections through communication that reflects craftsmanship, elegance and design philosophy.',
    approach: [
      'Translating the brand\'s aesthetic into a cohesive visual language',
      'Creative direction built around showcasing fabric textures and intricate detailing',
      'Highlighting the graceful silhouettes that define the collections',
      'Content strategy emphasising refined visual storytelling',
      'Maintaining a consistent and sophisticated brand tone'
    ],
    outcome: 'The resulting communication elevated House of Resham\'s digital presence, enabling the brand to present its collections with clarity and elegance.',
    services: ['Brand Identity', 'Social Media', 'Visual Storytelling', 'Content Strategy', 'Digital Presence'],
    results: [
      { label: 'ONLINE SALES', value: '+95%', icon: ShoppingCart },
      { label: 'SOCIAL FOLLOWING', value: '150%', icon: Users },
      { label: 'ENGAGEMENT', value: '18.2%', icon: TrendingUp },
      { label: 'BRAND REACH', value: '800K+', icon: Eye }
    ],
    testimonial: 'House of Resham\'s digital presence now reflects the elegance and craftsmanship that defines every garment in their collection.'
  },
  'bagheera': {
    title: 'THE BAGHEERA HERITAGE',
    tagline: 'Heritage Luxury Redefined',
    industry: 'HOSPITALITY | BOUTIQUE HERITAGE STAY',
    heroImage: '/bagheera-heritage-guests.jpg',
    galleryImages: ['/bagheera-heritage-guests.jpg', '/bagheera-horses.jpg', '/client-bagheera-property.jpg'],
    youtubeVideo: 'https://www.youtube.com/embed/nLp3R_8cCFQ',
    description: 'The Bagheera Heritage is a boutique hospitality property located in Jaipur, designed to offer guests a tranquil retreat rooted in heritage architecture and natural surroundings.',
    objective: 'Develop a distinctive digital identity for the property and position it as a refined heritage retreat within Jaipur\'s growing boutique hospitality landscape.',
    approach: [
      'Capturing the character and atmosphere that define The Bagheera Heritage',
      'Visual communication highlighting architectural detailing and landscaped surroundings',
      'Showcasing curated hospitality experiences offered by the property',
      'Structured digital storytelling emphasizing the calm, immersive environment',
      'Presenting it as an inviting destination for authentic stay experiences'
    ],
    outcome: 'The resulting communication strengthened the property\'s digital presence and helped establish The Bagheera Heritage as a distinctive boutique hospitality destination.',
    services: ['Brand Identity', 'Digital Marketing', 'Visual Content', 'Social Media', 'Direct Booking Optimisation'],
    results: [
      { label: 'DIRECT BOOKINGS', value: '+125%', icon: Calendar },
      { label: 'SOCIAL FOLLOWING', value: '300%', icon: Users },
      { label: 'ENGAGEMENT RATE', value: '15.8%', icon: TrendingUp },
      { label: 'BRAND REACH', value: '1.2M+', icon: Eye }
    ],
    testimonial: 'Team Zenith translated our hospitality philosophy into a cohesive digital narrative. Their strategic clarity elevated The Bagheera\'s positioning across platforms. - Akshay Singhi'
  },
  'garden-leaf': {
    title: 'THE GARDEN LEAF',
    tagline: 'Nature Meets Gastronomy',
    industry: 'HOSPITALITY | RESTAURANT & DINING',
    heroImage: '/garden-leaf-pasta.jpg',
    galleryImages: ['/garden-leaf-pasta.jpg', '/garden-leaf-thai.jpg', '/garden-leaf-interior.jpg'],
    youtubeVideo: 'https://www.youtube.com/embed/m_TVjRosXu8',
    description: 'The Garden Leaf is a contemporary dining destination designed as a calm and inviting space where food, ambience and conversation come together.',
    objective: 'Develop engaging digital communication that reflects the restaurant\'s tranquil atmosphere and positions it as a welcoming dining destination.',
    approach: [
      'Translating the restaurant\'s ambience into visually appealing digital storytelling',
      'Content designed to highlight signature dishes and warm interiors',
      'Showcasing the natural setting that defines the experience',
      'Consistent visual language across social media',
      'Emphasising freshness, comfort and a pleasant dining environment'
    ],
    outcome: 'The communication strengthened the restaurant\'s digital presence, helping it build stronger engagement with local audiences.',
    services: ['Social Media Management', 'Content Creation', 'Visual Storytelling', 'Local Marketing', 'Reputation Management'],
    results: [
      { label: 'SOCIAL ENGAGEMENT', value: '8.2%', icon: TrendingUp },
      { label: 'MONTHLY REVENUE', value: '+65%', icon: ShoppingCart },
      { label: 'CUSTOMER REVIEWS', value: '4.7★', icon: Eye },
      { label: 'BRAND REACH', value: '400K+', icon: Users }
    ],
    testimonial: 'The Garden Leaf has become a preferred dining destination, with digital communication that perfectly captures its tranquil ambience.'
  },
  'golden-locks': {
    title: 'GOLDEN LOCKS',
    tagline: 'Premium Architectural Hardware',
    industry: 'ARCHITECTURE & PREMIUM HARDWARE',
    heroImage: '/goldenlocks-railing.jpg',
    galleryImages: ['/goldenlocks-railing.jpg', '/goldenlocks-handles.jpg', '/client-goldenlocks-handles.jpg'],
    youtubeVideo: 'https://www.youtube.com/embed/hndJ5AejKhw',
    description: 'Golden Locks is a luxury architectural hardware brand specialising in high-quality railings, glass systems and architectural fittings designed for modern spaces.',
    objective: 'Strengthen the brand\'s digital presence while generating qualified enquiries from architects, builders and homeowners.',
    approach: [
      'Structured social media communication combined with performance-driven digital advertising',
      'Content focused on highlighting product craftsmanship and installation aesthetics',
      'Showcasing real-world architectural applications',
      'Performance marketing campaigns deployed to reach architects and interior designers',
      'Continuous campaign optimisation and creative testing'
    ],
    outcome: 'The integrated approach enhanced Golden Locks\' digital visibility while helping the brand connect with relevant professional audiences.',
    services: ['Social Media', 'Performance Marketing', 'Content Creation', 'Lead Generation', 'B2B Marketing'],
    results: [
      { label: 'QUALIFIED LEADS', value: '+180%', icon: Users },
      { label: 'ENGAGEMENT', value: '14%', icon: TrendingUp },
      { label: 'BRAND REACH', value: '500K+', icon: Eye },
      { label: 'B2B ENQUIRIES', value: '+120%', icon: BarChart3 }
    ],
    testimonial: 'Zenith understood our product depth and premium positioning. Their communication framework highlighted craftsmanship, resulting in stronger brand recall. - Vikalp Gupta'
  },
  'focus-apparels': {
    title: 'FOCUS APPARELS',
    tagline: 'Style Redefined',
    industry: 'FASHION & APPAREL',
    heroImage: '/focus-apparels-new.jpg',
    description: 'Focus Apparels is a contemporary clothing brand built around refined design, accessible fashion and everyday elegance.',
    objective: 'Develop a cohesive brand presentation that would elevate the perception of the label while maintaining strong retail relevance.',
    approach: [
      'Translating the brand\'s design sensibility into clear and engaging visual communication',
      'Developing marketing creatives, catalogue layouts and brand visuals',
      'Highlighting the craftsmanship, colour palette and styling of garments',
      'Careful attention to typography, composition and product presentation',
      'Ensuring every visual asset consistently reflected the brand\'s identity'
    ],
    outcome: 'The resulting communication established a more refined brand presence, allowing Focus Apparels to present its collections with greater clarity and visual impact.',
    services: ['Brand Strategy', 'Visual Identity', 'Marketing Creatives', 'Catalogue Design', 'Social Media'],
    results: [
      { label: 'ONLINE SALES', value: '+120%', icon: ShoppingCart },
      { label: 'SOCIAL GROWTH', value: '200%', icon: Users },
      { label: 'ENGAGEMENT', value: '14%', icon: TrendingUp },
      { label: 'BRAND REACH', value: '750K+', icon: Eye }
    ],
    testimonial: 'Zenith Marketing continues to support brands like Focus Apparels in building communication that balances aesthetic expression with commercial effectiveness.'
  }
};

const CaseStudyDetailPage = () => {
  const { slug } = useParams();
  const study = caseStudiesData[slug];

  const currentIndex = caseStudyOrder.indexOf(slug);
  const prevSlug = currentIndex > 0 ? caseStudyOrder[currentIndex - 1] : null;
  const nextSlug = currentIndex < caseStudyOrder.length - 1 ? caseStudyOrder[currentIndex + 1] : null;
  const prevStudy = prevSlug ? caseStudiesData[prevSlug] : null;
  const nextStudy = nextSlug ? caseStudiesData[nextSlug] : null;

  if (!study) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="text-center">
          <h1 className="font-outfit text-4xl font-bold text-gray-900 mb-4 uppercase">CASE STUDY NOT FOUND</h1>
          <Link to="/case-studies" className="text-brand-blue hover:text-cyan-500 transition-colors uppercase">
            ← BACK TO ALL WORK
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full pt-20">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={study.heroImage} alt={study.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div {...fadeInUp}>
            <Link to="/case-studies" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors uppercase">
              <ArrowLeft size={20} className="mr-2" />
              BACK TO ALL WORK
            </Link>
            <span className="block text-brand-blue text-sm font-semibold uppercase tracking-wider mb-4">
              {study.industry}
            </span>
            <h1 className="font-outfit text-5xl md:text-7xl font-bold text-white mb-4 uppercase">
              {study.title}
            </h1>
            <p className="text-2xl md:text-3xl text-white/80 font-light max-w-2xl">
              {study.tagline}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="max-w-4xl">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">OVERVIEW</span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-gray-900 mb-6 uppercase">PROJECT OVERVIEW</h2>
            <p className="text-xl text-gray-600 leading-relaxed">{study.description}</p>
          </motion.div>

          <motion.div {...fadeInUp} className="mt-10 flex flex-wrap gap-3">
            {study.services.map((service, idx) => (
              <span key={idx} className="px-4 py-2 bg-gradient-to-r from-brand-blue/10 to-cyan-400/10 rounded-full text-sm font-medium text-brand-blue border border-brand-blue/20 uppercase">
                {service}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      {study.galleryImages && study.galleryImages.length > 1 && (
        <section className="py-16 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {study.galleryImages.map((img, idx) => (
                <motion.div key={idx} variants={staggerItem} className="relative overflow-hidden rounded-2xl aspect-square">
                  <img src={img} alt={`${study.title} - ${idx + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Objective & Approach */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div {...fadeInUp}>
              <div className="w-16 h-16 bg-gradient-to-br from-brand-blue/10 to-cyan-400/10 rounded-2xl flex items-center justify-center mb-6">
                <Target className="text-brand-blue w-8 h-8" />
              </div>
              <h2 className="font-outfit text-3xl font-bold text-gray-900 mb-6 uppercase">THE OBJECTIVE</h2>
              <p className="text-xl text-gray-600 leading-relaxed">{study.objective}</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-gradient-to-br from-gray-50 to-gray-100 p-10 rounded-3xl border border-gray-200"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-brand-blue/10 to-cyan-400/10 rounded-xl flex items-center justify-center mb-6">
                <Lightbulb className="text-brand-blue w-7 h-7" />
              </div>
              <h3 className="font-outfit text-2xl font-bold text-gray-900 mb-6 uppercase">OUR APPROACH</h3>
              <ul className="space-y-4">
                {study.approach.map((item, idx) => (
                  <li key={idx} className="flex items-start">
                    <CheckCircle2 className="text-brand-blue flex-shrink-0 mt-1 mr-3 w-5 h-5" />
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-gradient-to-br from-brand-blue/10 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">IMPACT</span>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white uppercase">THE RESULTS</h2>
          </motion.div>

          <motion.div {...staggerContainer} className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {study.results.map((result, idx) => {
              const Icon = result.icon;
              return (
                <motion.div key={idx} variants={staggerItem} className="bg-white/5 backdrop-blur-sm p-8 rounded-2xl text-center border border-white/10 hover:bg-white/10 transition-all duration-300">
                  <div className="w-14 h-14 bg-gradient-to-br from-brand-blue/20 to-cyan-400/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-brand-blue w-7 h-7" />
                  </div>
                  <div className="font-outfit text-4xl font-bold bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent mb-2">{result.value}</div>
                  <div className="text-white/60 text-sm uppercase">{result.label}</div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* YouTube Video */}
      {study.youtubeVideo && (
        <section className="py-24 md:py-32 bg-gradient-to-b from-gray-900 to-gray-800">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fadeInUp}>
              <div className="text-center mb-10">
                <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-4 uppercase">WATCH THE STORY</h2>
                <p className="text-white/60">See the brand experience come to life</p>
              </div>
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                <iframe src={study.youtubeVideo} title={`${study.title} Video`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Outcome */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-brand-blue/10 to-cyan-400/10 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <TrendingUp className="text-brand-blue w-8 h-8" />
            </div>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-gray-900 mb-6 uppercase">THE OUTCOME</h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-10">{study.outcome}</p>
            <blockquote className="bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent text-lg italic font-medium border-l-4 border-brand-blue pl-6 text-left">
              "{study.testimonial}"
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12 bg-gradient-to-b from-gray-50 to-gray-100 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {prevStudy ? (
              <Link to={`/case-studies/${prevSlug}`} className="group flex items-center text-gray-600 hover:text-brand-blue transition-colors">
                <ChevronLeft className="mr-2 group-hover:-translate-x-1 transition-transform" size={24} />
                <div className="text-left">
                  <div className="text-sm text-gray-500 uppercase">PREVIOUS</div>
                  <div className="font-semibold text-gray-900 group-hover:text-brand-blue uppercase">{prevStudy.title}</div>
                </div>
              </Link>
            ) : <div />}

            <Link to="/case-studies" className="hidden md:flex items-center text-gray-500 hover:text-brand-blue transition-colors uppercase">
              VIEW ALL WORK
            </Link>

            {nextStudy ? (
              <Link to={`/case-studies/${nextSlug}`} className="group flex items-center text-gray-600 hover:text-brand-blue transition-colors">
                <div className="text-right">
                  <div className="text-sm text-gray-500 uppercase">NEXT</div>
                  <div className="font-semibold text-gray-900 group-hover:text-brand-blue uppercase">{nextStudy.title}</div>
                </div>
                <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" size={24} />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-brand-blue via-cyan-500 to-brand-blue relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-white/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-outfit text-2xl font-bold text-white mb-2 uppercase">WANT SIMILAR RESULTS FOR YOUR BRAND?</h3>
              <p className="text-white/70">Let's discuss how we can help you achieve your goals.</p>
            </div>
            <Link to="/contact" className="inline-flex items-center px-8 py-4 rounded-full bg-white text-brand-blue font-medium hover:bg-gray-100 transition-all duration-300 uppercase">
              GET IN TOUCH
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyDetailPage;
